import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { useDispatch, useSelector } from 'react-redux';
import { updateMainFilter } from '../mainFilterSlice';
import translate from '../../../utilities/translator';
import { scrollToResults, findInArray } from '../../../utilities/helpers';
import {
  setLastChangedFilter,
  setShowAirportPopup,
  selectFromWhereHompageActiveId,
  setFromWhereHompageActiveId,
  selectAppState,
} from '../../../appSlice';
import { selectFilterData } from '../selectors';
import { selectSecondaryFilter } from '../../secondaryFilter/secondaryFilterSlice';
import { pushEvent } from '../../../utilities/analytics';
import {
  ENGINE_ORIGIN_OPENED,
  ENGINE_ORIGIN_APPLIED,
  LOOKING_FOR_ANOTHER_ORIGIN_CLICKED,
} from '../../../constants/analyticEvents';

function DepartureDropdown() {
  const ref = useRef();
  const dispatch = useDispatch();
  const {
    searchEngine: { fromwhere: departures },
  } = useSelector(selectFilterData);
  const { showResults } = useSelector(selectAppState);
  const fromWhereHompageActiveId = useSelector(selectFromWhereHompageActiveId);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const mainFilter = useSelector(selectFilterData);
  const [dropDownOpen, setDropdownOpen] = useState(false);
  let departuresMutable = departures;
  if (!showResults && fromWhereHompageActiveId) {
    departuresMutable = departuresMutable.map((item) => ({ ...item, active: fromWhereHompageActiveId === item.id }));
  }
  const selectedDeparture = departuresMutable.find((item) => item.active) || departuresMutable[0];

  let departuresSorted = [...departuresMutable];
  // put (saved) pre-selected fromWhere to the first place in the list of departures
  if (
    departuresSorted.length > 1 &&
    localStorage.getItem('selectedFromwhere') &&
    JSON.parse(localStorage.getItem('selectedFromwhere')).date > new Date().getTime()
  ) {
    const selectedFromWhereId = JSON.parse(localStorage.getItem('selectedFromwhere'))?.fromwhere[0]?.id;
    if (selectedFromWhereId) {
      departuresSorted = [
        ...departuresSorted.filter((elem) => elem.id === selectedFromWhereId),
        ...departuresSorted.filter((elem) => elem.id !== selectedFromWhereId),
      ];
    }
  }

  useOnClickOutside(ref, () => dropDownOpen && setDropdownOpen(false));

  const onItemClick = (id) => {
    const updatedDepartures = departuresMutable.map((departure) => ({
      ...departure,
      active: id === departure.id,
    }));

    // set selected fromWhere to the localStorage for the further pre-selecting
    const selectedFromwhere = updatedDepartures.filter((item) => item.active);
    const fromwhereEndDate = new Date();
    fromwhereEndDate.setDate(fromwhereEndDate.getDate() + 30);
    localStorage.setItem(
      'selectedFromwhere',
      JSON.stringify({ fromwhere: selectedFromwhere, date: fromwhereEndDate.getTime() })
    );
    if (showResults) {
      dispatch(updateMainFilter({ filter: 'fromwhere', updatedValue: updatedDepartures }));
      dispatch(setLastChangedFilter('fromwhere'));
      scrollToResults();
    } else {
      const selectedIds = selectedFromwhere.map(item => item.id);
      dispatch(setFromWhereHompageActiveId(selectedIds));
    }
    setDropdownOpen(false);

    pushEvent(ENGINE_ORIGIN_APPLIED, {
      activeDeparture: updatedDepartures.filter((i) => i.active)[0],
      previousDeparture: selectedDeparture,
    });
  };

  useEffect(() => {
    if (dropDownOpen) {
      pushEvent(ENGINE_ORIGIN_OPENED);
    }
  }, [dropDownOpen]);

  useEffect(() => {
    if (fromWhereHompageActiveId && fromWhereHompageActiveId.length) {
      const updatedDepartures = departuresMutable.map((departure) => ({
        ...departure,
        active: findInArray(fromWhereHompageActiveId, departure.id),
      }));
      dispatch(setFromWhereHompageActiveId([]));
      dispatch(updateMainFilter({ filter: 'fromwhere', updatedValue: updatedDepartures }));
      dispatch(setLastChangedFilter('fromwhere'));
      scrollToResults();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryFilter.data, mainFilter.searchEngine]);

  return (
    <div className="departing" ref={ref}>
      <div className="departing__content">
        <i className="ri-flight-takeoff-line plane" />
        {translate('holidayFinder.mainFilter.departing_from')}

        {departuresMutable.length === 1 ? (
          <div className="departing__value">
            <span>{` ${selectedDeparture?.value}`}</span>
          </div>
        ) : (
          <>
            <div className="departing__value departing__value__selected" onClick={() => setDropdownOpen(!dropDownOpen)}>
              <span>{` ${selectedDeparture?.value}`}</span>
              <i className="ri-arrow-drop-down-line chevron" />
            </div>
            <div className={classNames('departing__dropdown', { 'is-open': dropDownOpen })}>
              <h6 className="departing__dropdown__title">{translate('holidayFinder.mainFilter.departing_title')}</h6>
              {departuresSorted.map((item, index) => (
                <div
                  key={index}
                  className={classNames('departing__dropdown-item', { active: item.id === selectedDeparture.id })}
                  onClick={() => onItemClick(item.id)}
                >
                  {item.value} {item.id === selectedDeparture.id ? <i className="ri-checkbox-circle-fill" /> : null}
                </div>
              ))}
              <p className="departing__dropdown__bottom">
                {translate('holidayFinder.mainFilter.departing_bottom')}{' '}
                <span
                  onClick={() => {
                    dispatch(setShowAirportPopup(true));
                    setDropdownOpen(!dropDownOpen);
                    pushEvent(LOOKING_FOR_ANOTHER_ORIGIN_CLICKED);
                  }}
                >
                  {translate('holidayFinder.mainFilter.different_airport')}
                </span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(DepartureDropdown);
